import type { OnlineDevice } from '@/@types/device';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import { OnDemandRouteStatus, type OnDemandRoute } from '@/@types/on-demand-route';

export type OnDemandServiceState = 'live' | 'noVehicle' | 'noService';

/** Without an answer for that long, the positions on the map are no longer live (hypothesis to confirm). */
export const CONNECTION_LOST_AFTER_MS = 30000;

/**
 * When the positions shown were last received, once the polling has stopped answering.
 * Counted from the restart too: back from the background, the poll has not had its
 * chance to answer yet. Nothing is lost before a first answer: there is nothing to dim.
 */
export function connectionLostSince(
  answeredAt: number | undefined,
  pollingSince: number,
  nowMs: number,
): number | undefined {
  if (answeredAt === undefined) return undefined;
  return nowMs - Math.max(answeredAt, pollingSince) > CONNECTION_LOST_AFTER_MS ? answeredAt : undefined;
}

/** The validity bounds are inclusive days; `today` is a GTFS date (YYYYMMDD). */
export function isOnDemandRouteRunning(route: OnDemandRoute, today: string): boolean {
  if (route.status !== OnDemandRouteStatus.ACTIVE) return false;
  const from = route.valid_from.replace(/-/g, '');
  const until = route.valid_until.replace(/-/g, '');
  return today >= from && today <= until;
}

/**
 * Which label the route carries. A vehicle seen on the line wins over the period:
 * it is the passenger's best information, whatever the configuration says.
 */
export function onDemandServiceState(
  route: OnDemandRoute,
  today: string,
  vehicleCount: number,
): OnDemandServiceState {
  if (vehicleCount > 0) return 'live';
  return isOnDemandRouteRunning(route, today) ? 'noVehicle' : 'noService';
}

/** A vehicle waiting for its trip to start is not serving the line yet. */
export function isServingOnDemandRoute(device: OnlineDevice, routeId?: string): boolean {
  if (!device.on_demand_route_id || device.trip_pending) return false;
  return routeId === undefined || device.on_demand_route_id === routeId;
}

export function countOnDemandVehicles(devices: OnlineDevice[]): Map<string, number> {
  const counts = new Map<string, number>();
  devices.forEach(device => {
    if (!isServingOnDemandRoute(device)) return;
    const routeId = device.on_demand_route_id!;
    counts.set(routeId, (counts.get(routeId) ?? 0) + 1);
  });
  return counts;
}

export function isOnDemandRoute(element: Route | Stop | OnDemandRoute): element is OnDemandRoute {
  return 'valid_from' in element && 'stops' in element;
}
